import { useState } from 'react'
import { Button, cn } from '@wellesley/ui'
import { UploadItem } from '@wellesley/uploads'

type AttachmentsTabProps = {
  attachments: UploadItem[]
  onInsert: (selectedIds: string[], attachments: UploadItem[]) => void
}

export const AttachmentsTab = ({
  attachments,
  onInsert,
}: AttachmentsTabProps) => {
  const [selectedAttachments, setSelectedAttachments] = useState<Set<string>>(
    new Set(),
  )

  const toggleAttachmentSelection = (id: string) => {
    setSelectedAttachments((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleInsert = () => {
    if (selectedAttachments.size > 0) {
      onInsert(Array.from(selectedAttachments), attachments)
    }
  }

  if (attachments.length === 0) {
    return (
      <div className="space-y-s">
        <div className="text-sm text-gray-500">No attachments available</div>
      </div>
    )
  }

  return (
    <div className="space-y-s">
      <div className="text-sm text-gray-600">Select attachments to insert</div>
      <div className="space-y-xs max-h-[300px] overflow-y-auto">
        {attachments.map((attachment) => {
          const id = attachment.uploadId || attachment.id
          const isSelected = selectedAttachments.has(id)
          return (
            <label
              key={id}
              className={cn(
                'gap-xs p-xs flex cursor-pointer items-center rounded border transition-colors hover:bg-gray-50',
                isSelected && 'border-blue-500 bg-blue-50',
              )}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleAttachmentSelection(id)}
                className="h-4 w-4"
              />
              <img
                src={attachment.preview}
                alt={attachment.meta?.name || 'Attachment'}
                className="h-12 w-12 rounded object-cover"
              />
              <span className="flex-1 truncate text-sm">
                {attachment.meta?.name || 'Untitled'}
              </span>
            </label>
          )
        })}
      </div>
      <Button
        onClick={handleInsert}
        disabled={selectedAttachments.size === 0}
        className="w-full"
      >
        Insert {selectedAttachments.size > 0 && `(${selectedAttachments.size})`}
      </Button>
    </div>
  )
}
